/**
 * MultiSelect Component
 *
 * A dropdown that allows selecting multiple options with
 * Select All / Deselect All functionality.
 */

import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Check,
  X,
  CheckSquare,
  Square,
  Search,
} from "lucide-react";

export interface MultiSelectOption {
  value: string;
  label: string;
  color?: string;
  indent?: number;
  searchText?: string;
}

export interface MultiSelectProps {
  label?: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  maxDisplay?: number;
  /** Accent color for focus states (defaults to indigo) */
  accentColor?: "indigo" | "red" | "blue" | "green";
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Render the dropdown in a portal so it can overlay overflow:hidden parents (e.g. modals) */
  menuPortal?: boolean;
  /** Hide selected chips under the field */
  hideSelectedTags?: boolean;
  /** Keep selected chips on one line and show the rest as +N */
  singleLineTags?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder = "Select...",
  disabled = false,
  className = "",
  maxDisplay = 2,
  accentColor = "red",
  searchable = false,
  searchPlaceholder = "Search...",
  menuPortal = false,
  hideSelectedTags = false,
  singleLineTags = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [portalPosition, setPortalPosition] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const tagMeasureRef = useRef<HTMLDivElement>(null);
  const [visibleTagCount, setVisibleTagCount] = useState(0);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener("pointerdown", handleClickOutside);
    return () =>
      document.removeEventListener("pointerdown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !menuPortal) {
      setPortalPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPortalPosition({
        left: rect.left,
        top: rect.bottom + 4,
        width: rect.width,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, menuPortal]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!searchable || !normalizedQuery) return options;
    return options.filter((option) =>
      (option.searchText || option.label).toLowerCase().includes(normalizedQuery),
    );
  }, [options, searchable, searchQuery]);

  const filteredOptionValues = useMemo(
    () => filteredOptions.map((option) => option.value),
    [filteredOptions],
  );

  const selectedFilteredCount = useMemo(
    () =>
      filteredOptionValues.filter((optionValue) => value.includes(optionValue))
        .length,
    [filteredOptionValues, value],
  );

  const isAllSelected =
    filteredOptionValues.length > 0 &&
    selectedFilteredCount === filteredOptionValues.length;
  const isNoneSelected = selectedFilteredCount === 0;

  // Color classes based on accent color
  const colorClasses = {
    indigo: {
      ring: "focus:ring-indigo-500 focus:border-indigo-500",
      ringOpen: "ring-2 ring-indigo-500/20 border-indigo-500",
      badge: "bg-indigo-100 text-indigo-700",
      selectAll: "bg-indigo-100 text-indigo-700",
      itemSelected: "bg-indigo-50",
      checkbox: "bg-indigo-500 border-indigo-500",
      done: "text-indigo-600 hover:text-indigo-700",
    },
    red: {
      ring: "focus:ring-red-500 focus:border-red-500",
      ringOpen: "ring-2 ring-red-500/20 border-red-500",
      badge: "bg-red-100 text-[#c00035]",
      selectAll: "bg-red-100 text-[#c00035]",
      itemSelected: "bg-red-50",
      checkbox: "bg-red-500 border-red-500",
      done: "text-[#c00035] hover:text-[#c00035]",
    },
    blue: {
      ring: "focus:ring-blue-500 focus:border-blue-500",
      ringOpen: "ring-2 ring-blue-500/20 border-blue-500",
      badge: "bg-blue-100 text-blue-700",
      selectAll: "bg-blue-100 text-blue-700",
      itemSelected: "bg-blue-50",
      checkbox: "bg-blue-500 border-blue-500",
      done: "text-blue-600 hover:text-blue-700",
    },
    green: {
      ring: "focus:ring-green-500 focus:border-green-500",
      ringOpen: "ring-2 ring-green-500/20 border-green-500",
      badge: "bg-green-100 text-green-700",
      selectAll: "bg-green-100 text-green-700",
      itemSelected: "bg-green-50",
      checkbox: "bg-green-500 border-green-500",
      done: "text-green-600 hover:text-green-700",
    },
  };

  const colors = colorClasses[accentColor];

  const handleToggle = (optionValue: string) => {
    if (disabled) return;
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const handleSelectAll = () => {
    if (disabled) return;
    onChange(Array.from(new Set([...value, ...filteredOptionValues])));
  };

  const handleDeselectAll = () => {
    if (disabled) return;
    const filteredSet = new Set(filteredOptionValues);
    onChange(value.filter((selectedValue) => !filteredSet.has(selectedValue)));
  };

  const getDisplayText = () => {
    if (searchable && !disabled) return placeholder;
    if (value.length === 0) return placeholder;
    if (value.length === options.length && options.length > 1) return "All Selected";

    const selectedLabels = options
      .filter((o) => value.includes(o.value))
      .map((o) => o.label);

    if (selectedLabels.length <= maxDisplay) {
      return selectedLabels.join(", ");
    }

    return `${selectedLabels.slice(0, maxDisplay).join(", ")} +${selectedLabels.length - maxDisplay}`;
  };

  const selectedOptions = options.filter((o) => value.includes(o.value));
  const selectionKey = selectedOptions.map((option) => option.value).join("\0");

  useLayoutEffect(() => {
    if (!singleLineTags || hideSelectedTags || selectedOptions.length === 0) {
      return;
    }
    const row = tagMeasureRef.current;
    if (!row) return;

    const measure = () => {
      const chips = Array.from(row.querySelectorAll<HTMLElement>("[data-select-tag]"));
      const more = row.querySelector<HTMLElement>("[data-select-more]");
      const available = row.clientWidth;
      if (!available || chips.length === 0) return;
      const gap = 4;
      const moreWidth = (more?.offsetWidth ?? 32) + gap;
      let used = 0;
      let count = 0;
      for (let index = 0; index < chips.length; index += 1) {
        const width = chips[index].offsetWidth;
        const remaining = chips.length - (index + 1);
        const reserve = remaining > 0 ? moreWidth : 0;
        if (used + width + reserve > available) break;
        used += width + gap;
        count += 1;
      }
      setVisibleTagCount((current) => (current === count ? current : count));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [singleLineTags, hideSelectedTags, selectionKey, selectedOptions.length]);

  const dropdown = (
    <AnimatePresence>
      {isOpen && (!menuPortal || portalPosition) ? (
        <motion.div
          ref={menuRef}
          initial={{ opacity: 0, y: -8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.96 }}
          transition={{ duration: 0.15 }}
          className={`${
            menuPortal ? "fixed" : "absolute w-full mt-1"
          } z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden`}
          style={
            menuPortal && portalPosition
              ? {
                  left: portalPosition.left,
                  top: portalPosition.top,
                  width: portalPosition.width,
                  zIndex: 10050,
                }
              : undefined
          }
        >
          <div className="px-2 py-2 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
            <button
              type="button"
              onClick={handleSelectAll}
              disabled={isAllSelected || disabled}
              className={`
                  flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-all
                  ${
                    isAllSelected || disabled
                      ? `${colors.selectAll} cursor-default`
                      : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                  }
                `}
            >
              <CheckSquare size={14} />
              Select All
            </button>
            <button
              type="button"
              onClick={handleDeselectAll}
              disabled={isNoneSelected || disabled}
              className={`
                  flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-all
                  ${
                    isNoneSelected || disabled
                      ? "bg-gray-100 text-gray-400 cursor-default"
                      : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                  }
                `}
            >
              <Square size={14} />
              Clear All
            </button>
          </div>

          <div className="max-h-60 overflow-y-auto py-1">
            {options.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-400 text-center">
                No options available
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-400 text-center">
                No matching options
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = value.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleToggle(option.value)}
                    disabled={disabled}
                    className={`
                        w-full flex items-center gap-3 px-3 py-2.5 text-sm
                        transition-all hover:bg-gray-50
                        ${option.indent ? "pl-8" : ""}
                        ${disabled ? "opacity-60 cursor-not-allowed" : ""}
                        ${isSelected ? colors.itemSelected : ""}
                      `}
                  >
                    <div
                      className={`
                        w-4 h-4 rounded border-2 flex items-center justify-center transition-all
                        ${isSelected ? colors.checkbox : "border-gray-300"}
                      `}
                    >
                      {isSelected && (
                        <Check size={12} className="text-white" />
                      )}
                    </div>
                    {option.color && (
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: option.color }}
                      />
                    )}
                    <span
                      className={`flex-1 text-left ${option.indent ? "" : "capitalize"} ${
                        isSelected
                          ? "font-medium text-gray-900"
                          : option.indent
                            ? "text-amber-800"
                            : "text-gray-700"
                      }`}
                    >
                      {option.label}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {value.length > 0 && (
            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
              <div
                className={`flex items-center ${searchable ? "justify-end" : "justify-between"} text-xs text-gray-500`}
              >
                {!searchable && (
                  <span>
                    {selectedOptions.length} of {options.length} selected
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className={`${colors.done} font-medium`}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
          {label}
        </label>
      )}

      <div ref={containerRef} className="relative">
        {/* Trigger / Search */}
        {searchable && !disabled ? (
        <div
          className={`
            w-full flex items-center gap-2
            px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm
            focus-within:outline-none focus-within:ring-2 ${colors.ring}
            transition-all h-[42px]
            ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-gray-300"}
            ${isOpen ? colors.ringOpen : ""}
          `}
        >
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
              if (!isOpen && !disabled) setIsOpen(true);
            }}
            onFocus={() => !disabled && setIsOpen(true)}
            placeholder={searchPlaceholder}
            disabled={disabled}
            // min-w-0: an input's intrinsic width would otherwise push the
            // count badge outside the box in narrow columns
            className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm text-gray-700 placeholder:text-gray-400"
          />
          <div className="flex items-center gap-1 flex-shrink-0">
            {selectedOptions.length > 0 && !disabled && (
              <span
                className={`px-1.5 py-0.5 ${colors.badge} text-xs font-medium rounded whitespace-nowrap`}
              >
                {selectedOptions.length} 
              </span>
            )}
            <button
              type="button"
              onClick={() => !disabled && setIsOpen(!isOpen)}
              disabled={disabled}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ChevronDown
                size={16}
                className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        </div>
        ) : (
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            w-full flex items-center justify-between gap-2
            px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm
            focus:outline-none focus:ring-2 ${colors.ring}
            transition-all h-[42px]
            ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-gray-300"}
            ${isOpen ? colors.ringOpen : ""}
          `}
        >
          <span
            className={`truncate capitalize ${value.length === 0 ? "text-gray-400" : "text-gray-900"}`}
          >
            {getDisplayText()}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            {selectedOptions.length > 0 && !disabled && (
              <span
                className={`px-1.5 py-0.5 ${colors.badge} text-xs font-medium rounded`}
              >
                {selectedOptions.length}
              </span>
            )}
            <ChevronDown
              size={16}
              className={`text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            />
          </div>
        </button>
        )}

        {menuPortal && typeof document !== "undefined"
          ? createPortal(dropdown, document.body)
          : dropdown}
      </div>

      {value.length > 0 && !hideSelectedTags && !disabled && (singleLineTags || searchable || value.length <= 3) && (
        singleLineTags ? (
          <div className="relative mt-2">
            <div
              ref={tagMeasureRef}
              aria-hidden
              className="pointer-events-none invisible absolute inset-x-0 flex flex-nowrap gap-1"
            >
              {selectedOptions.map((option) => (
                <span
                  key={option.value}
                  data-select-tag
                  className="inline-flex max-w-[9rem] shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs"
                >
                  <span className="min-w-0 truncate">{option.label}</span>
                  <X size={12} />
                </span>
              ))}
              <span data-select-more className="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium">
                +{selectedOptions.length}
              </span>
            </div>
            <div className="flex flex-nowrap items-center gap-1 overflow-hidden">
              {selectedOptions.slice(0, visibleTagCount).map((option) => (
                <span
                  key={option.value}
                  className="inline-flex max-w-[9rem] shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                  title={option.label}
                >
                  {option.color && (
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: option.color }}
                    />
                  )}
                  <span className="min-w-0 truncate">{option.label}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(option.value);
                    }}
                    className="hover:text-[#c00035] transition-colors"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              {selectedOptions.length > visibleTagCount && (
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                  +{selectedOptions.length - visibleTagCount}
                </span>
              )}
            </div>
          </div>
        ) : (
        (!searchable ? value.length <= 3 : true) && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedOptions.map((option) => (
            <span
              key={option.value}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full"
            >
              {option.color && (
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: option.color }}
                />
              )}
              {option.label}
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(option.value);
                  }}
                  className="hover:text-[#c00035] transition-colors"
                >
                  <X size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
        )
        )
      )}
    </div>
  );
};

export default MultiSelect;
